import type { UserSession } from '@/types/auth';
import type { LocationQuery, RouteParams } from 'vue-router';

interface StorageData<T> {
  data: T;
  timestamp: number;
  version: string;
}

interface StoredRoute {
  path: string;
  query?: LocationQuery;
  params?: RouteParams;
}

interface StorageKeys {
  SESSION: 'mcp_session';
  AUTH_TOKEN: 'mcp_auth_token';
  USER_PREFERENCES: 'mcp_user_preferences';
  LAST_ACTIVITY: 'mcp_last_activity';
  INTENDED_ROUTE: 'mcp_intended_route';
  THEME: 'mcp_theme';
  LANGUAGE: 'mcp_language';
}

const STORAGE_KEYS: StorageKeys = {
  SESSION: 'mcp_session',
  AUTH_TOKEN: 'mcp_auth_token',
  USER_PREFERENCES: 'mcp_user_preferences',
  LAST_ACTIVITY: 'mcp_last_activity',
  INTENDED_ROUTE: 'mcp_intended_route',
  THEME: 'mcp_theme',
  LANGUAGE: 'mcp_language',
};

class StorageService {
  private isStorageAvailable: boolean;

  constructor() {
    this.isStorageAvailable = this.checkStorageAvailability();
  }

  private checkStorageAvailability(): boolean {
    try {
      const test = 'storage_test';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      console.warn('localStorage is not available');
      return false;
    }
  }

  private setItem<T>(key: string, value: T): void {
    if (!this.isStorageAvailable) return;

    try {
      const storageData: StorageData<T> = {
        data: value,
        timestamp: Date.now(),
        version: '1.0',
      };
      const serializedValue = JSON.stringify(storageData);
      localStorage.setItem(key, serializedValue);
    } catch (error) {
      console.error(`Failed to store ${key}:`, error);
    }
  }

  getItem<T>(key: string): T | null {
    if (!this.isStorageAvailable) return null;

    try {
      const item = localStorage.getItem(key);
      if (!item) return null;

      const parsed: StorageData<T> = JSON.parse(item);

      // Check if item has our expected structure
      if (parsed && typeof parsed === 'object' && 'data' in parsed) {
        return parsed.data as T;
      }

      // Legacy support for old storage format
      return parsed as T;
    } catch (error) {
      console.error(`Failed to retrieve ${key}:`, error);
      return null;
    }
  }

  private removeItem(key: string): void {
    if (!this.isStorageAvailable) return;

    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Failed to remove ${key}:`, error);
    }
  }

  // Session management
  setSession(session: UserSession): void {
    console.log('StorageService: Setting session for user:', session.username, session.telegramId);
    this.setItem(STORAGE_KEYS.SESSION, session);
    this.setLastActivity(new Date());
  }

  getSession(): UserSession | null {
    const session = this.getItem<UserSession>(STORAGE_KEYS.SESSION);
    if (session) {
      console.log(
        'StorageService: Retrieved session for user:',
        session.username,
        'expires:',
        session.expiresAt
      );
    } else {
      console.log('StorageService: No session found in storage');
    }
    return session;
  }

  clearSession(): void {
    console.log('StorageService: Clearing session from storage');
    this.removeItem(STORAGE_KEYS.SESSION);
    this.removeItem(STORAGE_KEYS.LAST_ACTIVITY);
  }

  isSessionValid(): boolean {
    const session = this.getSession();
    if (!session) return false;

    try {
      const now = new Date();
      const expiresAt = new Date(session.expiresAt);

      return expiresAt > now && session.isAuthenticated === true;
    } catch (error) {
      console.error('Error validating session:', error);
      return false;
    }
  }

  // Auth token management
  setAuthToken(token: string, expirationHours = 720): void {
    // Store auth token with expiration (default 30 days)
    console.log(
      'StorageService: Setting persistent auth token (length:',
      token.length,
      'preview:',
      token.substring(0, 10) + '...)'
    );
    const tokenData = {
      token: token,
      expiresAt: new Date(Date.now() + expirationHours * 60 * 60 * 1000).toISOString(),
      createdAt: new Date().toISOString(),
    };

    if (this.isStorageAvailable) {
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, JSON.stringify(tokenData));
    }
  }

  getAuthToken(): string | null {
    if (!this.isStorageAvailable) return null;

    try {
      const tokenDataStr = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      if (!tokenDataStr) {
        console.log('StorageService: No auth token found');
        return null;
      }

      // Handle legacy plain string tokens
      if (!tokenDataStr.startsWith('{')) {
        console.log('StorageService: Found legacy plain token, converting to new format');
        const token = tokenDataStr;
        this.setAuthToken(token); // Convert to new format
        return token;
      }

      const tokenData = JSON.parse(tokenDataStr);

      // Check if token is expired
      if (new Date(tokenData.expiresAt) <= new Date()) {
        console.log('StorageService: Auth token expired, clearing');
        this.clearAuthToken();
        return null;
      }

      console.log('StorageService: Retrieved valid auth token, expires:', tokenData.expiresAt);
      return tokenData.token;
    } catch (error) {
      console.error('StorageService: Error parsing auth token, clearing:', error);
      this.clearAuthToken();
      return null;
    }
  }

  clearAuthToken(): void {
    const hadToken = this.hasAuthToken();
    console.log('StorageService: Clearing persistent auth token (had token:', hadToken, ')');
    this.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    if (hadToken) {
      console.log('StorageService: Persistent auth token successfully cleared');
    }
  }

  // Helper method to safely get and clear auth token
  consumeAuthToken(): string | null {
    console.log(
      'StorageService: consumeAuthToken called - but tokens are now persistent, using getAuthToken instead'
    );
    return this.getAuthToken();
  }

  // Helper method to check if auth token exists without consuming it
  hasAuthToken(): boolean {
    const hasToken = !!this.getAuthToken();
    return hasToken;
  }

  // Get auth token expiration info
  getAuthTokenInfo(): {
    token: string;
    expiresAt: string;
    createdAt: string;
    isExpired: boolean;
  } | null {
    if (!this.isStorageAvailable) return null;

    try {
      const tokenDataStr = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      if (!tokenDataStr) return null;

      if (!tokenDataStr.startsWith('{')) {
        // Legacy token
        return {
          token: tokenDataStr,
          expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          createdAt: new Date().toISOString(),
          isExpired: false,
        };
      }

      const tokenData = JSON.parse(tokenDataStr);
      return {
        ...tokenData,
        isExpired: new Date(tokenData.expiresAt) <= new Date(),
      };
    } catch (error) {
      console.error('StorageService: Error getting token info:', error);
      return null;
    }
  }

  // User preferences
  storeUserPreferences(preferences: Record<string, unknown>): void {
    this.setItem(STORAGE_KEYS.USER_PREFERENCES, preferences);
  }

  getUserPreferences(): Record<string, unknown> | null {
    return (
      this.getItem(STORAGE_KEYS.USER_PREFERENCES) || {
        theme: 'system',
        language: 'en',
        notifications: {
          email: true,
          telegram: true,
          apiLimits: true,
          securityAlerts: true,
          featureUpdates: false,
        },
        privacy: {
          shareUsageStatistics: false,
          allowAnalytics: false,
          publicProfile: false,
        },
        ui: {
          compactMode: false,
          showAdvancedFeatures: false,
          defaultToolView: 'grid',
          autoSaveInputs: true,
        },
      }
    );
  }

  updateUserPreference(key: string, value: unknown): void {
    const preferences = this.getUserPreferences() || {};
    preferences[key] = value;
    this.storeUserPreferences(preferences);
  }

  // Last activity tracking
  setLastActivity(date: Date): void {
    this.setItem(STORAGE_KEYS.LAST_ACTIVITY, date.toISOString());
  }

  getLastActivity(): Date | null {
    const activity = this.getItem<string>(STORAGE_KEYS.LAST_ACTIVITY);
    return activity ? new Date(activity) : null;
  }

  // Intended route for post-login redirect
  setIntendedRoute(route: StoredRoute): void {
    console.log('StorageService: Setting intended route:', route.path);
    this.setItem(STORAGE_KEYS.INTENDED_ROUTE, route);
  }

  getIntendedRoute(): StoredRoute | null {
    const route = this.getItem(STORAGE_KEYS.INTENDED_ROUTE) as StoredRoute | null;
    if (route) {
      console.log('StorageService: Retrieved intended route:', route.path);
    }
    return route;
  }

  clearIntendedRoute(): void {
    this.removeItem(STORAGE_KEYS.INTENDED_ROUTE);
  }

  // Helper method to safely get and clear intended route
  consumeIntendedRoute(): StoredRoute | null {
    const route = this.getIntendedRoute();
    if (route) {
      console.log('StorageService: Consuming intended route (will be cleared):', route.path);
      this.removeItem(STORAGE_KEYS.INTENDED_ROUTE);
    }
    return route;
  }

  // Store intended route from current location
  storeCurrentAsIntended(currentRoute: StoredRoute): void {
    // Don't store auth route as intended route
    if (currentRoute.path !== '/auth') {
      this.setIntendedRoute(currentRoute);
    }
  }

  // Temporary item storage (for auth session IDs, etc.)
  setTemporaryItem<T>(key: string, value: T): void {
    this.setItem(key, value);
  }

  getTemporaryItem<T = unknown>(key: string): T | null {
    return this.getItem<T>(key);
  }

  // Favorite tools management
  getFavoriteTools(): string[] {
    return this.getItem<string[]>('favorite_tools') || [];
  }

  setFavoriteTools(favorites: string[]): void {
    this.setItem('favorite_tools', favorites);
  }

  // Theme management
  setTheme(theme: 'light' | 'dark' | 'system'): void {
    this.setItem(STORAGE_KEYS.THEME, theme);
  }

  getTheme(): 'light' | 'dark' | 'system' {
    return this.getItem<'light' | 'dark' | 'system'>(STORAGE_KEYS.THEME) || 'system';
  }

  // Language management
  setLanguage(language: string): void {
    this.setItem(STORAGE_KEYS.LANGUAGE, language);
  }

  getLanguage(): string {
    return this.getItem<string>(STORAGE_KEYS.LANGUAGE) || 'en';
  }

  // Clear all application data
  clearAll(): void {
    if (!this.isStorageAvailable) return;

    Object.values(STORAGE_KEYS).forEach(key => {
      this.removeItem(key);
    });
  }

  // Export/Import functionality for data portability
  exportData(): string {
    if (!this.isStorageAvailable) return '{}';

    const data: Record<string, unknown> = {};

    Object.values(STORAGE_KEYS).forEach(key => {
      const value = this.getItem(key);
      if (value !== null) {
        data[key] = value;
      }
    });

    return JSON.stringify(
      {
        data,
        exportedAt: new Date().toISOString(),
        version: '1.0',
      },
      null,
      2
    );
  }

  importData(jsonData: string): { success: boolean; error?: string } {
    if (!this.isStorageAvailable) {
      return { success: false, error: 'Storage not available' };
    }

    try {
      const parsed = JSON.parse(jsonData);

      if (!parsed.data || typeof parsed.data !== 'object') {
        return { success: false, error: 'Invalid data format' };
      }

      // Clear existing data
      this.clearAll();

      // Import new data
      Object.entries(parsed.data).forEach(([key, value]) => {
        if (Object.values(STORAGE_KEYS).includes(key as keyof StorageKeys)) {
          this.setItem(key, value);
        }
      });

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to parse data',
      };
    }
  }

  // Storage usage information
  getStorageInfo(): { used: number; available: number; percentage: number } {
    if (!this.isStorageAvailable) {
      return { used: 0, available: 0, percentage: 0 };
    }

    try {
      let used = 0;

      // Calculate used storage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          const value = localStorage.getItem(key);
          if (value) {
            used += key.length + value.length;
          }
        }
      }

      // Estimate available storage (5MB typical limit)
      const estimated = 5 * 1024 * 1024; // 5MB in bytes
      const available = Math.max(0, estimated - used);
      const percentage = (used / estimated) * 100;

      return {
        used,
        available,
        percentage: Math.min(100, percentage),
      };
    } catch {
      return { used: 0, available: 0, percentage: 0 };
    }
  }

  // Cleanup old data based on timestamp
  cleanup(maxAge: number = 30 * 24 * 60 * 60 * 1000): void {
    // 30 days default
    if (!this.isStorageAvailable) return;

    const now = Date.now();

    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key) continue;

      try {
        const item = localStorage.getItem(key);
        if (!item) continue;

        const parsed = JSON.parse(item);

        // Check if item has timestamp and is old
        if (parsed && typeof parsed === 'object' && 'timestamp' in parsed) {
          if (now - parsed.timestamp > maxAge) {
            localStorage.removeItem(key);
          }
        }
      } catch {
        // Remove corrupted items
        localStorage.removeItem(key);
      }
    }
  }

  // Check if user has granted storage permission (for quota management)
  async requestPersistentStorage(): Promise<boolean> {
    if ('storage' in navigator && 'persist' in navigator.storage) {
      try {
        const granted = await navigator.storage.persist();
        return granted;
      } catch {
        return false;
      }
    }
    return false;
  }

  // Get storage quota information
  async getStorageQuota(): Promise<{ usage: number; quota: number } | null> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      try {
        const estimate = await navigator.storage.estimate();
        return {
          usage: estimate.usage || 0,
          quota: estimate.quota || 0,
        };
      } catch {
        return null;
      }
    }
    return null;
  }
}

// Create singleton instance
export const storageService = new StorageService();

// Run cleanup on initialization
storageService.cleanup();
